/**
 * Invitation_Accept_Screen.tsx — accept/decline a workspace invitation reached
 * via an email deep link (?mode=accept-invite&token=…). Styled to match the
 * SK Platform auth card.
 *
 * On mount, fetches GET /v1/invites/:token/preview (public, read-only) so the
 * screen renders the RIGHT state on first paint instead of guessing:
 *   - pending + new user   → name + phone + password form, company read-only
 *   - pending + user_exists → "sign in with your existing account" prompt
 *   - expired / accepted / declined / cancelled → matching terminal card
 *
 * On successful accept, redirects the browser straight into the inviting
 * product's app (resolveReturnTarget) instead of leaving the user on the
 * platform shell — falls back to enterApp() only if the product has no
 * allow-listed return target configured.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, KeyboardAvoidingView } from 'react-native';

import PrimaryButton from '../../components/primaryButton';
import PasswordInput from '../../components/passwordInput';
import CommonInput from '../../components/commonInput';
import MobileNumberInput, { COUNTRIES, CountryEntry } from '../../components/mobileNumberInput';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { useAuthFlow } from '../../navigation/authFlowContext';
import { acceptInvitation, declineInvitation, getInvitationPreview } from '../../services/auth/auth';
import { resolveReturnTarget } from '../../config/products';
import { setPendingInvite, takePendingInvite } from '../../navigation/pendingInvite';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useToast } from '../../components/toast';
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';
import * as SignupValidations from '../../validations/signupValidations';
import type { InvitationPreviewData } from '../../schema/auth/invitation';

type ScreenState = 'loading' | 'error' | 'ready';

const TERMINAL_COPY: Record<string, { title: string; sub: string }> = {
  expired: { title: 'Invitation expired', sub: 'This invitation link is no longer valid. Ask an admin to send a new one.' },
  accepted: { title: 'Already accepted', sub: 'This invitation has already been used. Just sign in to continue.' },
  declined: { title: 'Invitation declined', sub: 'This invitation was declined. Ask an admin to send a new one if that was a mistake.' },
  cancelled: { title: 'Invitation canceled', sub: 'This invitation was canceled by an admin in the workspace.' },
};

const InvitationAccept_Screen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'invitationAccept'>>();
  const { params } = useRoute<RouteProp<AuthStackParamList, 'invitationAccept'>>();
  const { theme } = useTheme();
  const { colors } = theme;
  const { enterApp } = useAuthFlow();
  const toast = useToast();
  const token = params?.token ?? '';

  const [screenState, setScreenState] = useState<ScreenState>('loading');
  const [preview, setPreview] = useState<InvitationPreviewData | null>(null);

  const defaultCountry = COUNTRIES.find((c) => c.code === 'IN') || COUNTRIES[0];
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    password: '',
    mobile: '',
    selectedCountry: defaultCountry,
  });
  const [touched, setTouched] = useState({ firstName: false, lastName: false, password: false, mobile: false });
  const [submitting, setSubmitting] = useState(false);
  // One resume per mount — the effect re-runs on state changes and must not
  // fire a second accept while the first is still in flight.
  const resumedRef = useRef(false);

  // Fetch the preview once we have a token. No token at all → invalid link,
  // same as a preview 404 — both are terminal, unrecoverable states.
  useEffect(() => {
    if (!token) {
      setScreenState('error');
      return;
    }
    let active = true;
    getInvitationPreview(token)
      .then((res) => {
        if (!active) return;
        setPreview(res.data);
        setScreenState('ready');
      })
      .catch(() => {
        if (active) setScreenState('error');
      });
    return () => { active = false; };
  }, [token]);

  const getFirstNameError = () => SignupValidations.getFirstNameError(form.firstName, touched.firstName);
  const getLastNameError = () => SignupValidations.getLastNameError(form.lastName, touched.lastName);
  const getMobileError = () => SignupValidations.getMobileError(form.mobile, touched.mobile, form.selectedCountry);
  const getPasswordError = () => SignupValidations.getPasswordError(form.password, touched.password);

  const formError = (): string | null =>
    getFirstNameError() ||
    getLastNameError() ||
    SignupValidations.getMobileError(form.mobile, true, form.selectedCountry) ||
    SignupValidations.getPasswordError(form.password, true) ||
    null;

  const filterNames = (raw: string) => raw.replace(/[^A-Za-z\s]/g, '');

  const redirectIntoProduct = async (product: string) => {
    const target = await resolveReturnTarget(product, null);
    if (target && Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(target);
      return;
    }
    // No allow-listed return target for this product (or native) — the safe
    // fallback is the platform shell itself, not a dead end.
    enterApp();
  };

  const acceptAsNewUser = async () => {
    if (submitting) return;
    setTouched({ firstName: true, lastName: true, password: true, mobile: true });
    const err = formError();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await acceptInvitation({
        token,
        password: form.password,
        first_name: form.firstName,
        last_name: form.lastName,
        country_code: form.selectedCountry.dialCode,
        phone_number: form.mobile,
      });
      showSuccess(res, 'Welcome aboard', 'Invitation accepted.');
      await redirectIntoProduct(preview?.product ?? 'platform');
    } catch (error: any) {
      handleApiError(error, 'Could not accept invitation', 'Please try again or request a new invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const acceptAsExistingUser = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await acceptInvitation({ token });
      showSuccess(res, 'Welcome aboard', 'Invitation accepted.');
      await redirectIntoProduct(preview?.product ?? 'platform');
    } catch (error: any) {
      // Most likely cause: no active session in this browser yet — the
      // caller must be authenticated for the existing-user path. Send them
      // to sign in; the invite link (still in their inbox) opens this same
      // screen again afterwards, now with a session cookie present.
      // No session in this browser yet. The existing-user path needs one — the
      // API matches the signed-in email against the invited address, and that
      // match is what proves the link reached its owner.
      //
      // So we hold the token and send them to sign in. Login resumes it on the way
      // out (see navigation/pendingInvite), which is the whole difference from
      // what this used to do: it told them to go back to their inbox and re-click
      // the link, at the exact moment they expected to be finished.
      if (error?.response?.status === 401) {
        setPendingInvite(token);
        navigation.navigate('login');
        return;
      }
      handleApiError(error, 'Could not accept invitation', 'Please try again or request a new invite.');
    } finally {
      setSubmitting(false);
    }
  };

  // Returning from sign-in. The person already pressed Accept once; making them
  // press it a second time to land where they were going is the same discourtesy
  // as sending them back to their inbox, just shorter.
  useEffect(() => {
    if (screenState !== 'ready' || !preview?.user_exists || submitting) return;
    if (!resumedRef.current && takePendingInvite() === token) {
      resumedRef.current = true;
      void acceptAsExistingUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenState, preview?.user_exists]);

  const decline = async () => {
    try { await declineInvitation(token); } finally { navigation.navigate('login'); }
  };

  const renderBody = () => {
    if (screenState === 'loading') {
      return <Text style={[s.sub, { color: colors.ink2 }]}>Checking your invitation…</Text>;
    }
    if (screenState === 'error' || !preview) {
      return (
        <>
          <Text style={[s.title, { color: colors.ink }]}>Invalid invitation link</Text>
          <Text style={[s.sub, { color: colors.ink2 }]}>
            This invitation link is broken or has already been used. Ask an admin to send a new one.
          </Text>
          <PrimaryButton title="Go to sign in" onPress={() => navigation.navigate('login')} hoverLift style={s.submit} textStyle={s.submitText} />
        </>
      );
    }

    const terminal = TERMINAL_COPY[preview.status];
    if (terminal) {
      return (
        <>
          <Text style={[s.title, { color: colors.ink }]}>{terminal.title}</Text>
          <Text style={[s.sub, { color: colors.ink2 }]}>{terminal.sub}</Text>
          <PrimaryButton title="Go to sign in" onPress={() => navigation.navigate('login')} hoverLift style={s.submit} textStyle={s.submitText} />
        </>
      );
    }

    if (preview.user_exists) {
      return (
        <>
          <Text style={[s.title, { color: colors.ink }]}>You're invited</Text>
          <Text style={[s.sub, { color: colors.ink2 }]}>
            Join <Text style={s.strong}>{preview.org_name}</Text> with your existing SK Platform account
            (<Text style={s.strong}>{preview.email}</Text>). You may be asked to sign in first —
            we will bring you straight back here.
          </Text>
          <PrimaryButton
            title="Accept invitation  →"
            onPress={acceptAsExistingUser}
            loading={submitting}
            hoverLift
            style={s.submit}
            textStyle={s.submitText}
          />
          <View style={s.footer}>
            <PrimaryButton title="Decline" onPress={decline} variant="ghost" size="small" textStyle={{ color: colors.ink3 }} />
          </View>
        </>
      );
    }

    // New user — fresh account, name + phone + password.
    return (
      <>
        <Text style={[s.title, { color: colors.ink }]}>You're invited</Text>
        <Text style={[s.sub, { color: colors.ink2 }]}>
          Create your account for <Text style={s.strong}>{preview.email}</Text> to get started.
        </Text>

        <CommonInput label="Company" value={preview.org_name} isReadOnly editable={false} />

        <View style={s.row}>
          <View style={[s.col, { marginRight: 8 }]}>
            <CommonInput
              label="First name"
              required
              value={form.firstName}
              onChangeText={(t: string) => setForm((f) => ({ ...f, firstName: filterNames(t) }))}
              touched={touched.firstName}
              error={getFirstNameError()}
              inputProps={{ onBlur: () => setTouched((t) => ({ ...t, firstName: true })) }}
            />
          </View>
          <View style={[s.col, { marginLeft: 8 }]}>
            <CommonInput
              label="Last name"
              required
              value={form.lastName}
              onChangeText={(t: string) => setForm((f) => ({ ...f, lastName: filterNames(t) }))}
              touched={touched.lastName}
              error={getLastNameError()}
              inputProps={{ onBlur: () => setTouched((t) => ({ ...t, lastName: true })) }}
            />
          </View>
        </View>

        <View style={{ marginTop: 4 }}>
          <MobileNumberInput
            required
            value={form.mobile}
            selectedCountry={form.selectedCountry}
            onCountryChange={(c: CountryEntry) => setForm((f) => ({ ...f, selectedCountry: c, mobile: '' }))}
            onChangeText={(t: string) => setForm((f) => ({ ...f, mobile: t }))}
            error={getMobileError()}
            onBlur={() => setTouched((t) => ({ ...t, mobile: true }))}
          />
        </View>

        <View style={{ marginBottom: 8, marginTop: 4 }}>
          <PasswordInput
            label="Create a password"
            placeholder="At least 8 characters"
            value={form.password}
            onChangeText={(t: string) => setForm((f) => ({ ...f, password: t }))}
            mode="signup"
            touched={touched.password}
            error={touched.password ? getPasswordError() : undefined}
            showRules
          />
        </View>

        <PrimaryButton title="Accept invitation  →" onPress={acceptAsNewUser} loading={submitting} hoverLift style={s.submit} textStyle={s.submitText} />
        <View style={s.footer}>
          <PrimaryButton title="Decline" onPress={decline} variant="ghost" size="small" textStyle={{ color: colors.ink3 }} />
        </View>
      </>
    );
  };

  return (
    <View style={[s.root, { backgroundColor: colors.paper }]}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={s.scrollContent} keyboardShouldPersistTaps="handled">
          <View style={[s.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
            <AuthBrandHeader />
            {renderBody()}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const s = StyleSheet.create({
  root: { flex: 1, ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}) },
  scrollContent: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 40, paddingHorizontal: 16 },
  card: {
    width: '100%', maxWidth: CONTENT.form, borderRadius: 24, padding: 32, borderWidth: 1, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 24px 60px -18px rgba(15,23,42,0.22)' } as any) : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 14 }, shadowOpacity: 0.14, shadowRadius: 32, elevation: 10 }),
  },
  title: { fontSize: 26, fontWeight: '900', marginBottom: 8, textAlign: 'center', letterSpacing: -0.8 },
  sub: { fontSize: 14, fontWeight: '500', marginBottom: 22, textAlign: 'center', lineHeight: 22 },
  strong: { fontWeight: '800' },
  row: { flexDirection: 'row', width: '100%' },
  col: { flex: 1 },
  submit: { marginTop: 8, height: 52, minHeight: 52, borderRadius: 14 },
  submitText: { fontSize: 15, letterSpacing: 1.2 },
  footer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 16 },
});

export default InvitationAccept_Screen;
